import { createPublicClient, http, type Abi, type Address, type Hash } from "viem";
import { getConnection, getWalletClient, switchChain } from "wagmi/actions";
import { robinhood } from "./config";
import { friendlyError } from "./errors";
import { toast } from "./toast";
import { wagmiConfig } from "./wagmi";
import { DEMO_ADDRESS, demoSimulate, demoTx, isDemo } from "./demo";

export const publicClient = createPublicClient({
  chain: robinhood,
  transport: http(undefined, { batch: true }),
  batch: { multicall: true },
});

export async function connectedAccount(): Promise<Address> {
  if (isDemo()) return DEMO_ADDRESS;
  const c = getConnection(wagmiConfig);
  if (!c.address) throw new Error("Connect your wallet first.");
  if (c.chainId !== robinhood.id) await switchChain(wagmiConfig, { chainId: robinhood.id });
  return c.address;
}

type WriteParams = {
  address: Address;
  abi: Abi;
  functionName: string;
  args?: readonly unknown[];
  value?: bigint;
};

/**
 * Simulates first (so the user sees a clear reason before signing anything), then sends,
 * waits for the receipt and reports progress with toasts. Returns the tx hash, or throws.
 */
export async function sendTx(label: string, p: WriteParams): Promise<Hash> {
  if (isDemo()) return demoTx(label, p);
  let id: number | undefined;
  try {
    const account = await connectedAccount();
    const { request } = await publicClient.simulateContract({ account, ...p } as never);
    const wallet = await getWalletClient(wagmiConfig, { chainId: robinhood.id });
    id = toast.show("pending", `${label}: confirm in your wallet`);
    const hash = await wallet.writeContract(request as never);
    toast.update(id, "pending", `${label}: confirming…`, hash);
    const receipt = await publicClient.waitForTransactionReceipt({ hash });
    if (receipt.status !== "success") throw new Error("The transaction reverted.");
    toast.update(id, "ok", `${label}: done`, hash);
    return hash;
  } catch (err) {
    const msg = friendlyError(err);
    if (id) toast.update(id, "err", msg);
    else toast.show("err", msg);
    throw err;
  }
}

/** Simulates a call from the connected account and returns its result (no signature). */
export async function simulate<T>(p: WriteParams): Promise<T> {
  if (isDemo()) return demoSimulate<T>(p);
  const account = await connectedAccount();
  const { result } = await publicClient.simulateContract({ account, ...p } as never);
  return result as T;
}
